import {JOB_TYPE} from "../constants/constants";

/**
 * Shared chunk structure
 */
export function cleanChunk(chunk = {}) {
    return {
        id: chunk.external_id,
        chunk_type: chunk.chunk_type,
        error: chunk.error ?? null,

        bbox_left: chunk.bbox_left,
        bbox_top: chunk.bbox_top,
        bbox_right: chunk.bbox_right,
        bbox_bottom: chunk.bbox_bottom,
    };
}

/**
 * Parse chunk structure
 */
export function cleanParseChunk(chunk = {}) {
    return {
        ...cleanChunk(chunk),
        html_content: chunk.html_content,
    };
}

/**
 * Parse page structure
 */
export function cleanParsePage(page = {}) {
    return {
        page_num: page.page_num,
        html_content: page.html_content,
        gcs_image_url: page.gcs_image_url,
        width_px: page.width_px,
        height_px: page.height_px,

        chunks: (page.chunks || []).map(
            cleanParseChunk
        ),
    };
}

/**
 * Deep extract chunk structure: reading order and word boxes
 */
export function cleanDeepExtractChunk(chunk = {}) {
    return {
        ...cleanParseChunk(chunk),
        num: chunk.num,
        words: (chunk.words || []).map((word) => ({
            text: word.text,
            bbox_left: word.bbox_left,
            bbox_top: word.bbox_top,
            bbox_right: word.bbox_right,
            bbox_bottom: word.bbox_bottom,
        })),
    };
}

/**
 * Deep extract chunks in reading order
 */
export function sortChunksByNum(chunks = []) {
    return [...chunks].sort((a, b) => (a.num ?? 0) - (b.num ?? 0));
}

/**
 * Deep extract page structure
 */
export function cleanDeepExtractPage(page = {}) {
    return {
        ...cleanParsePage(page),
        chunks: sortChunksByNum(page.chunks || []).map(
            cleanDeepExtractChunk
        ),
    };
}

/**
 * Split page structure
 */
export function cleanSplitPage(page = {}) {
    return {
        page_num: page.page_num,
        gcs_image_url: page.gcs_image_url,
        width_px: page.width_px,
        height_px: page.height_px,

        chunks: (page.chunks || []).map(
            cleanChunk
        ),
    };
}

/**
 * Parse pages
 */
export function cleanParsePages(pages = []) {
    return pages.map(cleanParsePage);
}

/**
 * Transcribe page structure
 */
export function cleanTranscribePage(page = {}) {
    return {
        page_num: page.page_num,
        text_content: page.text_content,
        gcs_image_url: page.gcs_image_url,
        width_px: page.width_px,
        height_px: page.height_px,
    };
}

/**
 * Split pages
 */
export function cleanSplitPages(pages = []) {
    return pages.map(cleanSplitPage);
}

/**
 * Transcribe pages
 */
export function cleanTranscribePages(pages = []) {
    return pages.map(cleanTranscribePage);
}

export const handleDownloadJson = (job) => {
    const data = buildExportJob(job)
    const blob = new Blob(
        [JSON.stringify(data, null, 2)],
        { type: "application/json" }
    );

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `job-${data?.name || "document"}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};


export function buildExportJob(job) {
    let pages = [];

    const fileName = job?.file?.name
        ?.replace(/[<>:"/\\|?*]/g, "_")
        ?.replace(/\.[^/.]+$/, "");

    if (job?.type === JOB_TYPE.PARSE) {
        pages = cleanParsePages(job?.file?.pages || []);
    } else if (job?.type === JOB_TYPE.DEEP_EXTRACT) {
        pages = (job?.file?.pages || []).map(cleanDeepExtractPage);
    } else if (job?.type === JOB_TYPE.TRANSCRIBE) {
        pages = cleanTranscribePages(job?.file?.pages || []);
    } else {
        pages = cleanSplitPages(job?.file?.pages || []);
    }

    return {
        type: job?.type,
        status: job?.status,
        name: fileName,

        file: {
            name: job?.file?.name,
            type: job?.file?.type,
            pages_count: job?.file?.pages_count,
            gcs_url: job?.file?.gcs_url,
            ...(job?.type === JOB_TYPE.DEEP_EXTRACT && {
                searchable_url: job?.file?.searchable_url,
            }),
            pages,
        },
    };
}

/**
 *
 * @param data
 * @param fileName
 * @param mimeType
 */
export const downloadBlobFile = (
    data,
    fileName,
    mimeType
) => {
    const blob = new Blob(
        [data],
        { type: mimeType }
    );

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);

    link.click();

    link.remove();

    window.URL.revokeObjectURL(url);
};